import { Shape, ShapeType } from '../../model/types';
import { rotatePoint } from './shapeTransform';
import { getPolygonBounds } from './shapeGeometry';

export interface ShapesBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
}

const boundsFromPoints = (points: number[]): ShapesBounds | null => {
  const bounds = getPolygonBounds(points);
  if (bounds.width === 0 && bounds.height === 0) return null;
  return {
    minX: bounds.minX,
    minY: bounds.minY,
    maxX: bounds.minX + bounds.width,
    maxY: bounds.minY + bounds.height,
    width: bounds.width,
    height: bounds.height,
  };
};

const getShapeBounds = (shape: Shape): ShapesBounds | null => {
  const rotation = shape.rotation ?? 0;
  const scaleX = shape.scaleX ?? 1;
  const scaleY = shape.scaleY ?? 1;

  if ('points' in shape) {
    return boundsFromPoints(
      shape.points.map((point, index) => {
        const pointIndex = Math.floor(index / 2) * 2;
        const scaledPoint = { x: shape.points[pointIndex] * scaleX, y: shape.points[pointIndex + 1] * scaleY };
        const rotated = rotatePoint(scaledPoint, { x: 0, y: 0 }, rotation);
        return index % 2 === 0 ? rotated.x : rotated.y;
      }),
    );
  }

  if (shape.type === ShapeType.CIRCLE) {
    const radiusX = shape.radiusX * scaleX;
    const radiusY = shape.radiusY * scaleY;
    const radians = (rotation * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const halfWidth = Math.sqrt(radiusX * radiusX * cos * cos + radiusY * radiusY * sin * sin);
    const halfHeight = Math.sqrt(radiusX * radiusX * sin * sin + radiusY * radiusY * cos * cos);

    return {
      minX: shape.x - halfWidth,
      minY: shape.y - halfHeight,
      maxX: shape.x + halfWidth,
      maxY: shape.y + halfHeight,
      width: halfWidth * 2,
      height: halfHeight * 2,
    };
  }

  if ('width' in shape && 'height' in shape) {
    const width = shape.width * scaleX;
    const height = shape.height * scaleY;
    return boundsFromPoints([
      shape.x,
      shape.y,
      rotatePoint({ x: shape.x + width, y: shape.y }, { x: shape.x, y: shape.y }, rotation).x,
      rotatePoint({ x: shape.x + width, y: shape.y }, { x: shape.x, y: shape.y }, rotation).y,
      rotatePoint({ x: shape.x + width, y: shape.y + height }, { x: shape.x, y: shape.y }, rotation).x,
      rotatePoint({ x: shape.x + width, y: shape.y + height }, { x: shape.x, y: shape.y }, rotation).y,
      rotatePoint({ x: shape.x, y: shape.y + height }, { x: shape.x, y: shape.y }, rotation).x,
      rotatePoint({ x: shape.x, y: shape.y + height }, { x: shape.x, y: shape.y }, rotation).y,
    ]);
  }

  return { minX: shape.x, minY: shape.y, maxX: shape.x, maxY: shape.y, width: 0, height: 0 };
};

export const getShapesBounds = (shapes: Shape[]): ShapesBounds | null => {
  if (!shapes.length) return null;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const shape of shapes) {
    const bounds = getShapeBounds(shape);
    if (!bounds) continue;

    minX = Math.min(minX, bounds.minX);
    minY = Math.min(minY, bounds.minY);
    maxX = Math.max(maxX, bounds.maxX);
    maxY = Math.max(maxY, bounds.maxY);
  }

  if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
    return null;
  }

  return { minX, minY, maxX, maxY, width: maxX - minX, height: maxY - minY };
};
